/**
 * Pure billing transforms — no React, no IO. The tested core of the feature.
 */
import type {
  AccessState,
  BillingInterval,
  BillingPlan,
  ServiceTier,
  Subscription,
  SubscriptionStatus,
} from "../types";

/** Display order for tier cards. */
export const TIER_ORDER: readonly ServiceTier[] = ["basic", "standard", "premium"];

export interface TierGroup {
  tier: ServiceTier;
  byInterval: Partial<Record<BillingInterval, BillingPlan>>;
}

/**
 * Group the flat plans list into one entry per tier (fixed order), indexed by
 * interval. Tiers with no plan are dropped so the UI never renders empty cards.
 */
export function groupPlansByTier(plans: BillingPlan[]): TierGroup[] {
  return TIER_ORDER.map((tier) => {
    const byInterval: Partial<Record<BillingInterval, BillingPlan>> = {};
    for (const plan of plans) {
      if (plan.tier === tier) byInterval[plan.interval] = plan;
    }
    return { tier, byInterval };
  }).filter((group) => Object.keys(group.byInterval).length > 0);
}

/**
 * Percent saved by paying annually vs twelve months at the monthly price, rounded.
 *
 * Computed live from the real Stripe amounts (not a hard-coded figure), so the
 * badge always reflects the actual prices. Returns null when either interval is
 * missing, the monthly price is unknown/zero, or annual isn't actually cheaper —
 * letting the card hide a meaningless or negative badge.
 */
export function annualSavingPercent(group: TierGroup): number | null {
  const monthly = group.byInterval.monthly?.unitAmount;
  const annual = group.byInterval.annual?.unitAmount;
  if (monthly == null || annual == null || monthly <= 0) return null;
  const pct = Math.round((1 - annual / (monthly * 12)) * 100);
  return pct > 0 ? pct : null;
}

/* ── Success-page polling ──────────────────────────────────────────────────── */

export const POLL_INTERVAL_MS = 2000;
/** ~24s ceiling — webhook lag is usually <5s; this is the safety net. */
export const MAX_POLL_ATTEMPTS = 12;

/**
 * Decide the next poll delay for the success page.
 * Returns false (stop) once access is granted or the attempt cap is hit.
 */
export function nextPollInterval(args: {
  status: SubscriptionStatus | undefined;
  attempts: number;
}): number | false {
  const { status, attempts } = args;
  if (status === "active" || status === "past_due") return false;
  if (attempts >= MAX_POLL_ATTEMPTS) return false;
  return POLL_INTERVAL_MS;
}

/* ── Dashboard view model ──────────────────────────────────────────────────── */

export type SubscriptionTone = "ok" | "warn" | "error" | "muted";

export interface SubscriptionView {
  kind: "empty" | "card";
  status: SubscriptionStatus | null;
  tier: ServiceTier | null;
  interval: BillingInterval | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  tone: SubscriptionTone;
  /** Show a "choose a plan" link (no active plan). */
  showChoosePlan: boolean;
  /** Show the "manage subscription" (portal) button — only when a row exists. */
  showManage: boolean;
  /**
   * Server-authoritative "lapsed" flag (accessState === "read_only"). Stripe's
   * `status` field can lag behind (webhook delay, stale mirror) — the dashboard
   * must never show an "Active" chip while access is actually read-only, so
   * every lapsed caller overrides `status`-derived display in favor of this.
   */
  lapsed: boolean;
}

/** Map a subscription (or its absence) to everything the dashboard renders. */
export function subscriptionView(sub: Subscription | null, accessState: AccessState): SubscriptionView {
  const lapsed = accessState === "read_only";
  if (sub === null) {
    return {
      kind: "empty",
      status: null,
      tier: null,
      interval: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
      tone: "muted",
      showChoosePlan: true,
      showManage: false,
      lapsed,
    };
  }
  const tone: SubscriptionTone = lapsed
    ? "error"
    : sub.status === "active"
      ? "ok"
      : sub.status === "past_due"
        ? "warn"
        : sub.status === "unpaid"
          ? "error"
          : "muted";
  return {
    kind: "card",
    status: sub.status,
    tier: sub.tier,
    interval: sub.interval,
    currentPeriodEnd: sub.currentPeriodEnd,
    cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
    tone,
    showChoosePlan: sub.status === "incomplete" || sub.status === "canceled",
    showManage: true,
    lapsed,
  };
}

/* ── Money ─────────────────────────────────────────────────────────────────── */

/**
 * Format a minor-unit (cents) amount as a localized currency string.
 * Stripe currency is lowercase ISO; Intl needs uppercase. Returns "" if unknown.
 */
export function formatPlanPrice(
  unitAmount: number | null,
  currency: string | null,
  locale: string,
): string {
  if (unitAmount === null || currency === null) return "";
  // `locale` carries the digit choice via its `-u-nu-` extension (from
  // resolveNumLocale) — pass it through verbatim so digits follow the user's
  // numberFormat preference, not the language. Do NOT override numberingSystem.
  const isArabic = locale.startsWith("ar");
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    // Default currencyDisplay ("symbol") renders Latin glyphs like "US$"/"UK£"
    // even in `ar` locale — use the Arabic currency name instead. This is a
    // language/text concern, so it stays keyed on the base locale's language.
    ...(isArabic && { currencyDisplay: "name" }),
  }).format(unitAmount / 100);
}
